import { useLoaderData, useNavigate } from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import { RepeatCashTransactionList } from "organisms/repeatcash-transaction-list/RepeatCashTransactionList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";

import { WalletRepeatCashCard } from "./RepeatCashCard";

type RepeatCashTransactionModel = Models<"RepeatCashTransactionModel">;

type LoaderData = Awaited<ReturnType<typeof walletRepeatCashPageLoader>>;
export const walletRepeatCashPageLoader = defineLoader(async () => {
  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 0,
  });

  const repeatCashTransactions = queryClient.fetchQuery(
    ["/repeat-cash/transactions"],
    () => get("/repeat-cash/transactions")
  );

  const platformMemberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );

  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  // Get user data first to access platform ID
  const user = await userInfo;

  const referralRewardsHistory = user?.platform?.id
    ? queryClient.fetchQuery(
        ["/referral-rewards/history", user.platform.id],
        () =>
          get("/referral-rewards/history", { platformId: user.platform!.id })
      )
    : Promise.resolve(null);

  return waitObj({
    userInfo,
    repeatCashTransactions,
    platformMemberships,
    platform,
    referralRewardsHistory,
  });
});

export const WalletRepeatCashPage = () => {
  const navigate = useNavigate();
  const {
    userInfo,
    repeatCashTransactions,
    platformMemberships,
    platform,
    referralRewardsHistory,
  } = useLoaderData() as LoaderData;
  const beautyBankFlag = useFeatureFlag("membershipBeautyBank");
  const platformHasBeautyBank = platformMemberships.some((membership) =>
    membership.membershipRewardGroups?.some(
      (rg) => rg.beautyBank?.creditAmount !== null
    )
  );
  const shouldShowRewards = platform?.platformSettings?.rewardsEnabled;

  const completedReferralUsers = (
    referralRewardsHistory?.referredUsers?.filter(
      (user) =>
        user.status === "Completed" && user.referrerReward.type === "RepeatCash"
    ) || []
  ).sort((a, b) => {
    const dateA = new Date(a.referrerReward.obtainedAt || a.dateJoined);
    const dateB = new Date(b.referrerReward.obtainedAt || b.dateJoined);
    return dateB.getTime() - dateA.getTime();
  });

  const availableReferralUsers = [...completedReferralUsers];

  const findReferralUserForTransaction = (
    transaction: RepeatCashTransactionModel
  ) => {
    if (!transaction.source?.referralRewardId) return null;

    const userIndex = availableReferralUsers.findIndex((user) => {
      const rewardDate = user.referrerReward.obtainedAt || user.dateJoined;
      const transactionDate = new Date(transaction.created || 0);
      const userRewardDate = new Date(rewardDate);

      const amountMatches =
        Math.abs(user.referrerReward.amount - transaction.amount) < 0.01;
      const datesDifferenceInDays =
        Math.abs(transactionDate.getTime() - userRewardDate.getTime()) /
        (1000 * 60 * 60 * 24);
      const datesAreClose = datesDifferenceInDays <= 7;

      return amountMatches && datesAreClose;
    });

    if (userIndex !== -1) {
      return availableReferralUsers.splice(userIndex, 1)[0];
    }

    return null;
  };

  const allTransactions = repeatCashTransactions
    .map((transaction) => {
      if (transaction.source?.referralRewardId) {
        const referralUser = findReferralUserForTransaction(transaction);
        if (referralUser) {
          const firstNameWithLastInitial = `${
            referralUser.firstName
          } ${referralUser.lastName.charAt(0)}.`;
          return {
            ...transaction,
            source: {
              ...transaction.source,
              title: `${firstNameWithLastInitial} completed their first check in`,
            },
          };
        }
      }
      return transaction;
    })
    .sort(
      (a, b) =>
        new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime()
    );

  const renderBeautyBankMessage = () => {
    let message =
      "Earn RepeatCash through rewards by checking in or shopping in the app—then spend it like cash for instant savings!";

    if (!shouldShowRewards) {
      message = "Apply RepeatCash to your next purchase for instant savings!";
    }

    if (beautyBankFlag && platformHasBeautyBank) {
      message =
        "Earn RepeatCash by checking in, shopping, or with select memberships—then use it like cash for instant savings!";
    }

    return (
      <div className="text-center text-body1 text-secondary">{message}</div>
    );
  };

  return (
    <div className="flex h-full flex-col gap-10 py-8">
      <div className="flex flex-col gap-4 px-8">
        <WalletRepeatCashCard
          amount={userInfo.repeatCashBalance}
          onClick={() => navigate("/shop")}
        />
        {renderBeautyBankMessage()}
      </div>
      <RepeatCashTransactionList
        transactions={allTransactions}
        platformMemberships={platformMemberships}
      />
    </div>
  );
};
